import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { AxiosApiError } from '@/types/apiError';
import { useMutation, useQueryClient, InfiniteData } from '@tanstack/react-query';
import { REPORT_GROUPS_QUERY_KEY } from './useInfiniteReportGroups';
import { REPORT_GROUP_ASSETS_QUERY_KEY } from './useListReportGroupAssets';
import { ListReportGroupsResponse, ReportGroup } from '@/api/generatedApi';
import { AxiosResponse } from 'axios';

export const useRemoveAssetFromGroup = ({
  reportId,
  groupId,
  onSuccess,
  onError,
}: {
  reportId: string;
  groupId: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();

  const tenantId = getTenantId();

  const { mutate, isPending } = useMutation({
    mutationFn: ({ assetId }: { assetId: string }) =>
      api.reports.removeAssetFromReportGroup(tenantId, reportId, groupId, assetId),
    onMutate: async ({ assetId }) => {
      const previousData = queryClient.getQueriesData({ queryKey: [REPORT_GROUPS_QUERY_KEY] });

      queryClient.setQueriesData<InfiniteData<AxiosResponse<ListReportGroupsResponse>>>(
        { queryKey: [REPORT_GROUPS_QUERY_KEY] },
        old => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              data: {
                ...page.data,
                groups: page.data.groups.map((group: ReportGroup) => {
                  if (group.id !== groupId) return group;
                  const updatedAssetIds = group.assetIds.filter(id => id !== assetId);
                  return { ...group, assetIds: updatedAssetIds };
                }),
              },
            })),
          };
        }
      );

      return { previousData };
    },
    onError: (error: AxiosApiError, _variables, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      onError?.(error.message || 'Failed to remove asset from group');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REPORT_GROUPS_QUERY_KEY] });
      queryClient.resetQueries({ queryKey: [REPORT_GROUP_ASSETS_QUERY_KEY], exact: false });
      onSuccess?.();
    },
  });

  return {
    mutate,
    isPending,
  };
};
